import type ApplicationModel from 'components/framework/ApplicationModel';
import type Filter from 'components/framework/filters/Filter';

export interface HasBookmarkFilters<
  Filters extends Record<string, Filter> = {}
> {
  getFilter<K extends keyof Filters>(scope: K): Filters[K] | undefined;
  writeFilterAttribute<K extends keyof Filters>(scope: K): void;
  writeFilterAttributes(): void;
  addFilterItem<K extends keyof Filters>(scope: K, item: any): void;
  deleteFilterItem<K extends keyof Filters>(scope: K, item: any): void;
  clearFilterItems<K extends keyof Filters>(scope: K): void;
  /**
   * Replaces a filter's items atomically.
   */
  setFilterItems<K extends keyof Filters>(scope: K, items: any[]): void;

  /**
   * Replaces a filter's value atomically
   */
  setFilter<K extends keyof Filters>(scope: K, value: any): void;
  canDeleteFilterItem<K extends keyof Filters>(scope: K): boolean;

  /**
   * Updates the list of all possible filter IDs with `options`. For
   * example, will tell the `UserFilter` about all users once the
   * Selectro is first opened.
   */
  setFilterOptions<K extends keyof Filters>(scope: K, options: {}): void;
}

/**
 * Extend a model with bookmark filters.
 */
// Overload 1: broad ClassDecorator compatibility
export function HasBookmarkFilters(originalClass: {
  new (...args: any[]): ApplicationModel & {
    filters: any;
    // Optional createFilters on base; if present, it must return a filter map
    createFilters?: () => Promise<Record<string, Filter>>;
    // Ensure the base exposes a record slot for filter objects
    local: { filterObjects?: Record<string, Filter> };
  };
}): any;
// Overload 2: strongly-typed mixin usage preserving constructor typing
export function HasBookmarkFilters<
  TBase extends new (...args: any[]) => ApplicationModel,
  F extends Record<string, Filter> = Record<string, Filter>
>(
  Base: TBase & {
    prototype: {
      filters: any;
      createFilters?: () => Promise<F>;
      local: { filterObjects?: Partial<F> };
    };
  }
): new (...args: ConstructorParameters<TBase>) => InstanceType<TBase> &
  HasBookmarkFilters<F>;

// Implementation
export function HasBookmarkFilters(originalClass: {
  new (...args: any[]): ApplicationModel & {
    filters: any;
    createFilters?: () => Promise<Record<string, Filter>>;
    local: { filterObjects?: Record<string, Filter> };
  };
}): any {
  if (originalClass.prototype.createFilters) {
    const oldCreateFilters = originalClass.prototype.createFilters as Function;
    originalClass.prototype.createFilters = async function (...args: any[]) {
      const filterObjects = (await oldCreateFilters.apply(
        this,
        args
      )) as Record<string, Filter>;
      this.local.filterObjects = filterObjects;
      this.writeFilterAttributes();
      return filterObjects;
    };
  }

  return class
    extends originalClass
    implements HasBookmarkFilters<Record<string, Filter>>
  {
    /**
     * Returns the Filter subclass for the specified filter `scope`
     */
    getFilter<K extends string>(scope: K) {
      const filterObjects = this.local.filterObjects ?? {};
      if (!this.local.filterObjects) this.local.filterObjects = filterObjects;
      return filterObjects[scope as string];
    }

    /**
     * Updates the server-side board state with the current filter state.
     */
    writeFilterAttribute(scope: string) {
      const filter = this.getFilter(scope);
      this.filters = {
        ...this.filters,
        [scope]: filter.toBookmarkConfiguration(),
      };
      // eslint-disable-next-line dot-notation
      if (super['writeFilterAttribute']) {
        // @ts-ignore - this can be added to the base class as an override
        super.writeFilterAttribute(scope);
      }
    }

    writeFilterAttributes(): void {
      Object.keys(this.local.filterObjects ?? {}).forEach(scope => {
        this.writeFilterAttribute(scope);
      });
    }

    /**
     * Adds an item to the specified filter.
     */
    addFilterItem(scope: string, item: any): void {
      (this.getFilter(scope) as any)?.addItem?.(item);
      this.writeFilterAttribute(scope);
    }

    /**
     * Removes an item from the specified filter.
     */
    deleteFilterItem(scope: string, item: any): void {
      (this.getFilter(scope) as any)?.deleteItem?.(item);
      this.writeFilterAttribute(scope);
    }

    /**
     * Removes all items from the filter, effectively making it inactive.
     */
    clearFilterItems(scope: string): void {
      (this.getFilter(scope) as any)?.clearItems?.();
      this.writeFilterAttribute(scope);
    }

    /**
     * Replaces a filter's items atomically.
     */
    setFilterItems(scope: string, items: any[]): void {
      (this.getFilter(scope) as any)?.setItems?.(items);
      this.writeFilterAttribute(scope);
    }

    /**
     * Replaces a filter's value atomically
     */
    setFilter(scope: string, value: any): void {
      (this.getFilter(scope) as any)?.set?.(value);
      this.writeFilterAttributes();
    }

    canDeleteFilterItem(scope: string): boolean {
      return Boolean((this.getFilter(scope) as any)?.hasItems);
    }

    /**
     * Updates the list of all possible filter IDs with `options`. For
     * example, will tell the `UserFilter` about all users once the
     * Selectro is first opened.
     */
    setFilterOptions(scope: string, options: {}): void {
      (this.getFilter(scope) as any)?.setOptions?.(options);
    }
  };
}
